import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Button, Badge, Form, InputGroup, Nav, Modal, Toast, ToastContainer, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch, faUserPlus, faCheckCircle, faTimesCircle, faClock, faRefresh, faExternalLinkAlt, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { callQuery, LIDIA_GET_ALL_PARTICIPANTS, LIDIA_GET_ACTIVE_PARTICIPANTS, LidiaParticipant, PATIENT_VISIT_BY_CSN } from '../../amplify/api-gateway/query';
import { callWrite, LIDIA_ENROLL_PARTICIPANT } from '../../amplify/api-gateway/write';
import { getLocalDateStrFromDate, getLocalTimeStrFromDate, parseAsUTC } from '../../utils/time';
import { PatientVisit } from '../../amplify/api-gateway/search-patients';

type FilterType = 'all' | 'active' | 'withdrawn' | 'completed';

const ToggleableDate = ({ date }: { date: Date }) => {
    const [showExact, setShowExact] = useState(false);

    const getRelativeDays = (date: Date) => {
        const now = new Date();
        const diffTime = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return 'today';
        } else if (diffDays === 1) {
            return '1 day ago';
        } else {
            return `${diffDays} days ago`;
        }
    };

    return (
        <span
            onClick={(e) => {
                e.stopPropagation();
                setShowExact(!showExact);
            }}
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            title="Click to toggle between relative and exact date"
            data-interactive="true"
        >
            {showExact
                ? `${getLocalDateStrFromDate(date)} ${getLocalTimeStrFromDate(date)}`
                : getRelativeDays(date)
            }
        </span>
    );
};

export const LidiaStudyDashboard = () => {
    const navigate = useNavigate();
    const [participants, setParticipants] = useState<LidiaParticipant[]>([]);
    const [filteredParticipants, setFilteredParticipants] = useState<LidiaParticipant[]>([]);
    const [filter, setFilter] = useState<FilterType>('active');
    const [showSimulated, setShowSimulated] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    // Enrollment modal state
    const [showEnrollModal, setShowEnrollModal] = useState(false);
    const [enrollForm, setEnrollForm] = useState({
        csn: '',
        mrn: '',
        phone_number: '',
        participant_type: 'SIMULATED',
        initiate_conversation: false
    });
    const [isEnrolling, setIsEnrolling] = useState(false);
    const [enrollError, setEnrollError] = useState('');

    // Toast state
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVariant, setToastVariant] = useState<'success' | 'danger'>('success');

    useEffect(() => {
        loadParticipants();
    }, [filter, showSimulated]);

    useEffect(() => {
        applyFilters();
    }, [participants, searchTerm]);

    const loadParticipants = async () => {
        setIsLoading(true);
        try {
            const operation = filter === 'active' ? LIDIA_GET_ACTIVE_PARTICIPANTS : LIDIA_GET_ALL_PARTICIPANTS;
            const participantType = showSimulated ? 'SIMULATED' : 'REAL';
            const data = await callQuery({
                operation,
                participant_type: participantType
            });
            setParticipants(data || []);
        } catch (error) {
            console.error('Error loading participants:', error);
            showErrorToast('Failed to load participants');
        } finally {
            setIsLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...participants];

        // Apply status filter (only needed for 'all' query)
        if (filter !== 'all' && filter !== 'active') {
            filtered = filtered.filter(p => p.status.toLowerCase() === filter);
        }

        // Apply search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(p =>
                p.study_id.toLowerCase().includes(term) ||
                p.mrn.toLowerCase().includes(term) ||
                p.csn.toLowerCase().includes(term) ||
                (p.phone_number && p.phone_number.includes(term))
            );
        }

        setFilteredParticipants(filtered);
    };

    const handleEnroll = async () => {
        setEnrollError('');

        // Validation
        if (!enrollForm.csn || !enrollForm.mrn) {
            setEnrollError('CSN and MRN are required');
            return;
        }

        // Validate phone number if provided
        if (enrollForm.phone_number) {
            const phoneRegex = /^\+1[0-9]{10}$/;
            if (!phoneRegex.test(enrollForm.phone_number)) {
                setEnrollError('Phone number must be in E.164 format (+16411222233)');
                return;
            }
        }

        setIsEnrolling(true);
        try {
            const result = await callWrite({
                operation: LIDIA_ENROLL_PARTICIPANT,
                csn: enrollForm.csn,
                mrn: enrollForm.mrn,
                phone_number: enrollForm.phone_number || undefined,
                participant_type: enrollForm.participant_type,
                status: 'ACTIVE'
            });

            showSuccessToast(`Participant enrolled successfully! Study ID: ${result.study_id}`);
            setShowEnrollModal(false);
            setEnrollForm({ csn: '', mrn: '', phone_number: '', participant_type: 'REAL', initiate_conversation: false });
            loadParticipants();
        } catch (error: any) {
            console.error('Error enrolling participant:', error);
            setEnrollError(error.message || 'Failed to enroll participant');
        } finally {
            setIsEnrolling(false);
        }
    };

    const showSuccessToast = (message: string) => {
        setToastMessage(message);
        setToastVariant('success');
        setShowToast(true);
    };

    const showErrorToast = (message: string) => {
        setToastMessage(message);
        setToastVariant('danger');
        setShowToast(true);
    };

    const getStatusBadge = (status: string) => {
        const variants: Record<string, string> = {
            ACTIVE: 'success',
            WITHDRAWN: 'warning',
            COMPLETED: 'secondary'
        };
        return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
    };

    const getConversationStateBadge = (state?: string | null) => {
        if (!state) return <Badge bg="secondary">No Conversation</Badge>;

        const variants: Record<string, string> = {
            IDLE: 'secondary',
            IN_PROGRESS: 'primary',
            CLOSED: 'dark'
        };
        return <Badge bg={variants[state] || 'secondary'}>{state}</Badge>;
    };

    const formatDate = (dateStr?: string | null) => {
        if (!dateStr) return 'N/A';
        try {
            const date = parseAsUTC(dateStr);
            return <ToggleableDate date={date} />;
        } catch {
            return dateStr;
        }
    };

    const redirectToDashboard = (csn: string) => {
        callQuery({
            operation: PATIENT_VISIT_BY_CSN,
            csn: csn,
        })
        .then((info: PatientVisit[]) => {
            if (info && info.length > 0 && info[0].internal_csn) {
                window.open(`/dashboard/${info[0].internal_csn}`, "_blank");
            }
        });
    };

    const handleRowClick = (studyId: string, e: React.MouseEvent) => {
        // Don't trigger row click if clicking on interactive elements
        const target = e.target as HTMLElement;

        // Check if click was on a link, button, or interactive element
        if (target.closest('a') || target.closest('button') || target.closest('[data-interactive]')) {
            return;
        }

        // Open participant details in new tab
        window.open(`/lidia-dashboard/${studyId}`, '_blank');
    };

    return (
        <Container fluid style={{marginTop: '80px'}}>
            <Row className="mb-4">
                <Col>
                    <h2>LIDIA Study Dashboard</h2>
                    <p className="text-muted">Manage study participants and track conversation activity</p>
                </Col>
                <Col xs="auto">
                    <div className="d-flex align-items-center gap-2">
                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip id="simulated-toggle-tooltip">
                                    Toggle between real participants (Production) and simulated/test participants (Test)
                                </Tooltip>
                            }
                        >
                            <div>
                                <Form.Check
                                    type="switch"
                                    id="simulated-toggle"
                                    label={showSimulated ? "Test" : "Production"}
                                    checked={!showSimulated}
                                    onChange={(e) => setShowSimulated(!e.target.checked)}
                                    className='me-2'
                                />
                            </div>
                        </OverlayTrigger>
                        <Button
                            variant="outline-secondary"
                            onClick={loadParticipants}
                            disabled={isLoading}
                        >
                            <FontAwesomeIcon icon={faRefresh} spin={isLoading} /> Refresh
                        </Button>
                        <Button variant="primary" onClick={() => setShowEnrollModal(true)}>
                            <FontAwesomeIcon icon={faUserPlus} /> Enroll New Participant
                        </Button>
                    </div>
                </Col>
            </Row>

            {/* Summary Stats */}
            {!isLoading && participants.length > 0 && (
                <Row className="mb-4">
                    <Col md={3}>
                        <Card className="text-center">
                            <Card.Body>
                                <h3 className="text-primary">{participants.filter(p => p.status === 'ACTIVE').length}</h3>
                                <p className="text-muted mb-0">Active</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center">
                            <Card.Body>
                                <h3 className="text-warning">{participants.filter(p => p.status === 'WITHDRAWN').length}</h3>
                                <p className="text-muted mb-0">Withdrawn</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center">
                            <Card.Body>
                                <h3 className="text-secondary">{participants.filter(p => p.status === 'COMPLETED').length}</h3>
                                <p className="text-muted mb-0">Completed</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center">
                            <Card.Body>
                                <h3>{participants.length}</h3>
                                <p className="text-muted mb-0">Total</p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            <Row className="mb-3">
                <Col md={6}>
                    <Nav variant="tabs" activeKey={filter} onSelect={(k) => setFilter(k as FilterType)}>
                        <Nav.Item>
                            <Nav.Link eventKey="active">Active</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="withdrawn">Withdrawn</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="completed">Completed</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="all">All</Nav.Link>
                        </Nav.Item>
                    </Nav>
                </Col>
                <Col md={6} className="d-flex gap-2">
                    <InputGroup>
                        <InputGroup.Text>
                            <FontAwesomeIcon icon={faSearch} />
                        </InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder="Search by Study ID, MRN, CSN, or Phone..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </InputGroup>
                </Col>
            </Row>

            <Row>
                <Col>
                    <Card>
                        <Card.Body>
                            {isLoading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" role="status" variant="primary">
                                        <span className="visually-hidden">Loading...</span>
                                    </Spinner>
                                    <p className="text-muted mt-3">Loading participants...</p>
                                </div>
                            ) : filteredParticipants.length === 0 ? (
                                <div className="text-center py-5">
                                    <p className="text-muted">No participants found</p>
                                    {filter !== 'all' && (
                                        <Button
                                            variant="link"
                                            onClick={() => setFilter('all')}
                                        >
                                            View all participants
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <Table responsive hover>
                                    <thead>
                                        <tr>
                                            <th>Study ID</th>
                                            <th>MRN</th>
                                            <th>CSN</th>
                                            <th>Phone Number</th>
                                            <th>Status</th>
                                            <th>Conversation</th>
                                            <th>Messages</th>
                                            <th>Alerts</th>
                                            <th>Enrollment Date</th>
                                            <th>Last Activity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredParticipants.map((participant) => (
                                            <tr
                                                key={participant.row_id}
                                                className={participant.unacknowledged_alerts && participant.unacknowledged_alerts > 0 ? 'table-warning' : ''}
                                                onClick={(e) => handleRowClick(participant.study_id, e)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <td><strong>{participant.study_id}</strong></td>
                                                <td>{participant.mrn}</td>
                                                <td>
                                                    {participant.csn ? (
                                                        <a
                                                            href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                redirectToDashboard(participant.csn);
                                                            }}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-decoration-none"
                                                            title="Open patient dashboard in new tab"
                                                        >
                                                            {participant.csn}{' '}
                                                            <FontAwesomeIcon
                                                                icon={faExternalLinkAlt}
                                                                size="sm"
                                                                className="text-muted"
                                                            />
                                                        </a>
                                                    ) : (
                                                        participant.csn
                                                    )}
                                                </td>
                                                <td>{participant.phone_number || 'N/A'}</td>
                                                <td>{getStatusBadge(participant.status)}</td>
                                                <td>{getConversationStateBadge(participant.conversation_state)}</td>
                                                <td>
                                                    {participant.message_count || 0}
                                                    {!!participant.error_count && participant.error_count > 0 && (
                                                        <Badge bg="danger" className="ms-1" title={participant.last_error || 'Error occurred'}>
                                                            {participant.error_count} errors
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td>
                                                    {!!participant.unacknowledged_alerts && participant.unacknowledged_alerts > 0 ? (
                                                        <Badge bg="warning" className="d-flex align-items-center gap-1 justify-content-center">
                                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                                            {participant.unacknowledged_alerts}
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-muted">None</span>
                                                    )}
                                                </td>
                                                <td>{formatDate(participant.enrollment_date)}</td>
                                                <td>{formatDate(participant.last_conversation_activity || participant.last_activity_date)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Enrollment Modal */}
            <Modal show={showEnrollModal} onHide={() => setShowEnrollModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Enroll New Participant</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={(e) => {
                        e.preventDefault();
                        handleEnroll();
                    }}>
                        <Form.Group className="mb-3" controlId="formCSN">
                            <Form.Label>CSN <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter CSN"
                                value={enrollForm.csn}
                                onChange={(e) => setEnrollForm({ ...enrollForm, csn: e.target.value })}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formMRN">
                            <Form.Label>MRN <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter MRN"
                                value={enrollForm.mrn}
                                onChange={(e) => setEnrollForm({ ...enrollForm, mrn: e.target.value })}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formPhone">
                            <Form.Label>Phone Number (Optional)</Form.Label>
                            <Form.Control
                                type="tel"
                                placeholder="+16411222233"
                                value={enrollForm.phone_number}
                                onChange={(e) => setEnrollForm({ ...enrollForm, phone_number: e.target.value })}
                            />
                            <Form.Text className="text-muted">
                                Phone number must be in E.164 format (e.g., +16411222233)
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formParticipantType">
                            <Form.Label>Participant Type <span className="text-danger">*</span></Form.Label>
                            <Form.Select
                                value={enrollForm.participant_type}
                                onChange={(e) => setEnrollForm({ ...enrollForm, participant_type: e.target.value })}
                                required
                            >
                                <option value="SIMULATED">Simulated/Test Participant</option>
                                <option value="REAL">Real Participant</option>
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Select whether this is a real participant enrollment or a test/simulated participant for testing purposes
                            </Form.Text>
                        </Form.Group>

                        {enrollError && (
                            <div className="alert alert-danger">{enrollError}</div>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEnrollModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleEnroll} disabled={isEnrolling}>
                        {isEnrolling ? 'Enrolling...' : 'Enroll Participant'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Toast Notification */}
            <ToastContainer position="top-end" className="p-3">
                <Toast
                    show={showToast}
                    onClose={() => setShowToast(false)}
                    delay={5000}
                    autohide
                    bg={toastVariant}
                >
                    <Toast.Header>
                        <strong className="me-auto">
                            {toastVariant === 'success' ? 'Success' : 'Error'}
                        </strong>
                    </Toast.Header>
                    <Toast.Body className="text-white">{toastMessage}</Toast.Body>
                </Toast>
            </ToastContainer>
        </Container>
    );
};

export default LidiaStudyDashboard;
